/**
 * useRoleNavigation Hook
 * Provides role-aware navigation functions for breadcrumbs and other navigation needs
 * Also provides permission checks for view-only roles like executive
 */
import { useCallback, useMemo } from 'react';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useAuth } from '../contexts/AuthContext';
import { MainStackParamList } from '../navigation/MainNavigator';

type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

export function useRoleNavigation() {
  const navigation = useNavigation<NavigationProp>();
  const { user } = useAuth();
  const userType = user?.profile?.user_type;
  const dataEntryOpen = user?.data_entry_open !== false;

  // ============================================================
  // Permission Flags for different roles
  // - org_executive: VIEW + APPROVE within own org (cannot add/edit)
  // - executive: VIEW only (cannot add/edit/delete/approve)
  // - org_employee: Can add/edit/delete (needs approval from org_executive)
  //   - When data entry is closed: can only enter KPI readings + comments
  // - strategy_team: VIEW only (can view all orgs but cannot add/edit/delete)
  // - superuser: Full access
  // ============================================================

  // Is data entry closed for this org_employee?
  const isDataEntryClosed = useMemo(() => {
    return userType === 'org_employee' && !dataEntryOpen;
  }, [userType, dataEntryOpen]);

  // Can add new items (superuser, org_employee when data entry is open)
  const canAdd = useMemo(() => {
    if (userType === 'superuser') return true;
    if (userType === 'org_employee') return dataEntryOpen;
    return false;
  }, [userType, dataEntryOpen]);

  // Can edit goals, initiatives, KPIs, etc. (superuser, org_employee when data entry is open)
  const canEdit = useMemo(() => {
    if (userType === 'superuser') return true;
    if (userType === 'org_employee') return dataEntryOpen;
    return false;
  }, [userType, dataEntryOpen]);

  // Can approve/reject goals, initiatives, etc. (only org_executive, superuser - NOT strategy_team)
  const canApprove = useMemo(() => {
    return userType === 'org_executive' || userType === 'superuser';
  }, [userType]);

  // Can delete items (superuser, org_employee when data entry is open)
  const canDelete = useMemo(() => {
    if (userType === 'superuser') return true;
    if (userType === 'org_employee') return dataEntryOpen;
    return false;
  }, [userType, dataEntryOpen]);

  // Is this user view-only? (strategy_team, executive, org_executive,
  // performance_evaluator, evaluation_approver, or org_employee with closed
  // data entry). Evaluation roles see strategic data the same way executives
  // do — read-only with the org filter visible.
  const isViewOnly = useMemo(() => {
    if (
      userType === 'executive' ||
      userType === 'org_executive' ||
      userType === 'strategy_team' ||
      userType === 'performance_evaluator' ||
      userType === 'evaluation_approver'
    ) return true;
    if (userType === 'org_employee' && !dataEntryOpen) return true;
    return false;
  }, [userType, dataEntryOpen]);

  // Is this a Decision Maker user? (متخذ القرار)
  // Executives and evaluation roles see strategic data the same way: only
  // approved entities are visible, no pending items, no edit/delete actions.
  const isDecisionMaker = useMemo(() => {
    return (
      userType === 'executive' ||
      userType === 'performance_evaluator' ||
      userType === 'evaluation_approver'
    );
  }, [userType]);

  // Is this an org_executive user? (مدير الجهة)
  const isOrgExecutive = useMemo(() => {
    return userType === 'org_executive';
  }, [userType]);

  // Is this an executive user? (متخذ القرار) — read-only viewer with
  // cross-org visibility but no scoring or numeric-score affordances.
  const isExecutive = useMemo(() => {
    return userType === 'executive';
  }, [userType]);

  // Is this a Strategy Team user (view all orgs, but no approval)?
  const isStrategyTeam = useMemo(() => {
    return userType === 'strategy_team' || userType === 'superuser';
  }, [userType]);

  // Is this an org_employee user?
  const isOrgEmployee = useMemo(() => {
    return userType === 'org_employee';
  }, [userType]);

  // Can view all organizations (strategy_team, superuser, executive,
  // performance_evaluator, evaluation_approver — NOT org_executive)
  const canViewAllOrganizations = useMemo(() => {
    return (
      userType === 'strategy_team' ||
      userType === 'superuser' ||
      userType === 'executive' ||
      userType === 'performance_evaluator' ||
      userType === 'evaluation_approver'
    );
  }, [userType]);

  // Can see pending items and approve/reject (only org_executive, superuser - NOT strategy_team, executive)
  const canSeePending = useMemo(() => {
    return userType === 'org_executive' || userType === 'superuser';
  }, [userType]);

  // Can see notifications (everyone except executive)
  const canSeeNotifications = useMemo(() => {
    return userType !== 'executive';
  }, [userType]);

  // Can view reports (strategy_team, superuser, executive, and org_executive)
  const canExport = useMemo(() => {
    return userType === 'strategy_team' || userType === 'superuser' || userType === 'executive' || userType === 'org_executive';
  }, [userType]);

  // Can add comments (org_employee, org_executive, strategy_team, superuser - NOT executive)
  const canComment = useMemo(() => {
    return userType === 'org_employee' || userType === 'org_executive' || userType === 'strategy_team' || userType === 'superuser';
  }, [userType]);

  /**
   * Navigate to home/dashboard based on user role
   * - org_employee, org_executive -> DashboardStack/Dashboard
   * - strategy_team/superuser/executive -> StrategyStack/StrategyDashboard
   */
  const navigateToHome = useCallback(() => {
    if (userType === 'strategy_team' || userType === 'superuser' || userType === 'executive') {
      navigation.navigate('StrategyStack' as any, { screen: 'StrategyDashboard' });
    } else {
      navigation.navigate('DashboardStack' as any, { screen: 'Dashboard' });
    }
  }, [navigation, userType]);

  /**
   * Navigate to Goals list based on user role
   * - org_employee -> GoalsStack
   * - strategy_team/superuser/executive -> StrategyStack with Goals screen
   */
  const navigateToGoals = useCallback(() => {
    if (userType === 'strategy_team' || userType === 'superuser' || userType === 'executive') {
      navigation.navigate('StrategyStack' as any, {
        screen: 'Goals',
      });
    } else {
      navigation.navigate('GoalsStack' as any, {
        screen: 'Goals',
      });
    }
  }, [navigation, userType]);

  /**
   * Navigate to GoalDetail based on user role
   * - org_employee -> GoalsStack
   * - strategy_team/superuser/executive -> StrategyStack
   */
  const navigateToGoalDetail = useCallback((goalId: number) => {
    if (userType === 'strategy_team' || userType === 'superuser' || userType === 'executive') {
      navigation.navigate('StrategyStack' as any, {
        screen: 'GoalDetail',
        params: { id: goalId },
      });
    } else {
      navigation.navigate('GoalsStack' as any, {
        screen: 'GoalDetail',
        params: { id: goalId },
      });
    }
  }, [navigation, userType]);

  /**
   * Navigate to Initiatives list
   */
  const navigateToInitiatives = useCallback(() => {
    navigation.navigate('InitiativesStack' as any, {
      screen: 'Initiatives',
    });
  }, [navigation]);

  /**
   * Navigate to InitiativeDetail
   */
  const navigateToInitiativeDetail = useCallback((initiativeId: number) => {
    navigation.navigate('InitiativesStack' as any, {
      screen: 'InitiativeDetail',
      params: { id: initiativeId },
    });
  }, [navigation]);

  /**
   * Navigate to KPIs list
   */
  const navigateToKPIs = useCallback(() => {
    navigation.navigate('GoalsStack' as any, {
      screen: 'Goals',
    });
  }, [navigation]);

  /**
   * Navigate to KPIDetail
   */
  const navigateToKPIDetail = useCallback((kpiId: number) => {
    navigation.navigate('GoalsStack' as any, {
      screen: 'KPIDetail',
      params: { id: kpiId },
    });
  }, [navigation]);

  /**
   * Navigate to Processes list
   */
  const navigateToProcesses = useCallback(() => {
    navigation.navigate('ProcessesStack' as any, {
      screen: 'Processes',
    });
  }, [navigation]);

  /**
   * Navigate to ProcessDetail
   */
  const navigateToProcessDetail = useCallback((processId: number) => {
    navigation.navigate('ProcessesStack' as any, {
      screen: 'ProcessDetail',
      params: { id: processId },
    });
  }, [navigation]);

  /**
   * Navigate to Services list
   */
  const navigateToServices = useCallback(() => {
    navigation.navigate('ServicesStack' as any, {
      screen: 'Services',
    });
  }, [navigation]);

  /**
   * Navigate to MyOrganization
   */
  const navigateToMyOrganization = useCallback(() => {
    navigation.navigate('MyOrganizationStack' as any, {
      screen: 'MyOrganization',
    });
  }, [navigation]);

  /**
   * Navigate to UnitGoals list
   */
  const navigateToUnitGoals = useCallback(() => {
    navigation.navigate('UnitGoalsStack' as any, {
      screen: 'UnitGoals',
    });
  }, [navigation]);

  /**
   * Navigate to Risks list
   */
  const navigateToRisks = useCallback(() => {
    navigation.navigate('RisksStack' as any, {
      screen: 'Risks',
    });
  }, [navigation]);

  /**
   * Check if user can access a route
   */
  const canAccess = useCallback((route: string): boolean => {
    const orgEmployeeRoutes = [
      'Dashboard', 'DashboardStack',
      'Goals', 'GoalsStack', 'GoalDetail', 'AddGoal',
      'Initiatives', 'InitiativesStack', 'InitiativeDetail', 'AddInitiative',
      'KPIs', 'KPIsStack', 'KPIDetail', 'AddKPIReading',
      'Processes', 'ProcessesStack', 'ProcessDetail', 'AddProcess',
      'Services', 'ServicesStack', 'ServiceDetail', 'AddService',
      'UnitGoals', 'UnitGoalsStack', 'UnitGoalDetail', 'AddUnitGoal',
      'Risks', 'RisksStack', 'AddRisk',
      'MyOrganization', 'MyOrganizationStack',
      'Notifications',
    ];

    const strategyTeamRoutes = [
      ...orgEmployeeRoutes,
      'StrategyStack', 'StrategyDashboard',
      'Organizations', 'OrganizationsStack', 'OrganizationDetail',
      'StrategyOrganizationsStack',
      'Schedules', 'SchedulesStack',
      'Export', 'ExportStack',
      'Admin', 'AdminStack',
    ];

    // org_executive has same VIEW access as org_employee but no Add/Edit routes
    // Uses DashboardStack with VIEW-ONLY permissions + approval capability (through Goals/Initiatives screens)
    const orgExecutiveRoutes = [
      'Dashboard', 'DashboardStack',
      'Goals', 'GoalsStack', 'GoalDetail',
      'Initiatives', 'InitiativesStack', 'InitiativeDetail',
      'KPIs', 'KPIsStack', 'KPIDetail',
      'Processes', 'ProcessesStack', 'ProcessDetail',
      'Services', 'ServicesStack', 'ServiceDetail',
      'UnitGoals', 'UnitGoalsStack', 'UnitGoalDetail',
      'Risks', 'RisksStack',
      'Employees', 'EmployeesStack',
      'MyOrganization', 'MyOrganizationStack',
      'Notifications',
      'Settings', 'SettingsStack',
      'Help', 'HelpStack',
    ];

    // executive has same VIEW access as strategy_team but no Add/Edit routes
    // Uses same StrategyStack but with VIEW-ONLY permissions
    const executiveRoutes = [
      'StrategyStack', 'StrategyDashboard',
      'Goals', 'GoalsStack', 'GoalDetail',
      'Initiatives', 'InitiativesStack', 'InitiativeDetail',
      'KPIs', 'KPIsStack', 'KPIDetail',
      'Organizations', 'OrganizationsStack', 'OrganizationDetail',
      'StrategyOrganizationsStack',
      'Risks', 'RisksStack',
      'Schedules', 'SchedulesStack',
      'Help', 'HelpStack',
    ];

    if (userType === 'superuser' || userType === 'strategy_team') {
      return strategyTeamRoutes.includes(route);
    }

    if (userType === 'org_executive') {
      return orgExecutiveRoutes.includes(route);
    }

    if (userType === 'executive') {
      return executiveRoutes.includes(route);
    }

    if (userType === 'org_employee') {
      if (!orgEmployeeRoutes.includes(route)) return false;
      // When data entry is closed, block Add/Edit routes (except AddKPIReading)
      if (!dataEntryOpen) {
        const blockedRoutes = ['AddGoal', 'AddInitiative', 'AddProcess', 'AddService', 'AddUnitGoal', 'AddRisk'];
        if (blockedRoutes.includes(route)) return false;
      }
      return true;
    }

    return false;
  }, [userType, dataEntryOpen]);

  return {
    // Navigation functions
    navigateToHome,
    navigateToGoals,
    navigateToGoalDetail,
    navigateToInitiatives,
    navigateToInitiativeDetail,
    navigateToKPIs,
    navigateToKPIDetail,
    navigateToProcesses,
    navigateToProcessDetail,
    navigateToServices,
    navigateToMyOrganization,
    navigateToUnitGoals,
    navigateToRisks,
    canAccess,
    // User type
    userType,
    // Permission flags for different roles
    canAdd,
    canEdit,
    canApprove,
    canDelete,
    isViewOnly,
    isDecisionMaker,
    isOrgExecutive,
    isExecutive,
    isStrategyTeam,
    isOrgEmployee,
    isDataEntryClosed,
    canViewAllOrganizations,
    canSeePending,
    canSeeNotifications,
    canExport,
    canComment,
  };
}
